// src/utils/userCache.js
// 用於快取 user 和 profile，避免重複查詢

let cachedUser = null;
let cachedProfile = null;
let userPromise = null;
let profilePromise = null;

/**
 * 取得快取的 user 和 profile
 * 如果快取不存在，會查詢並快取
 */
export const getCachedUserAndProfile = async (supabase) => {
  // 如果正在查詢中，等待查詢結果
  if (userPromise) {
    await userPromise;
    return { user: cachedUser, profile: cachedProfile };
  }
  
  // 🔑 如果已有快取，直接返回（支援嵌入模式）
  if (cachedUser && cachedProfile) {
    return { user: cachedUser, profile: cachedProfile };
  }
  
  // 🔑 如果只有 profile 快取（嵌入模式可能只設定 profile），生成一個簡單的 user 物件
  if (!cachedUser && cachedProfile) {
    cachedUser = {
      id: cachedProfile.id,
      email: cachedProfile.email,
      user_metadata: { name: cachedProfile.name || cachedProfile.email }
    };
    return { user: cachedUser, profile: cachedProfile };
  }
  
  // 查詢 user 和 profile（獨立模式）
  userPromise = (async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // 🔑 嵌入模式下，可能沒有 Supabase Auth user，但有手動設定的 profile
        if (cachedProfile) {
          cachedUser = {
            id: cachedProfile.id,
            email: cachedProfile.email,
            user_metadata: { name: cachedProfile.name || cachedProfile.email }
          };
          return;
        }
        
        cachedUser = null;
        cachedProfile = null;
        return;
      }
      
      cachedUser = user;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, email, name, avatar_url, role, company_id, department_id')
        .eq('id', user.id)
        .single();
      
      cachedProfile = profile;
    } catch (error) {
      console.error('查詢 user/profile 失敗:', error);
      
      // 🔑 即使查詢失敗，如果有手動設定的 profile（嵌入模式），也可以使用
      if (cachedProfile) {
        cachedUser = {
          id: cachedProfile.id,
          email: cachedProfile.email,
          user_metadata: { name: cachedProfile.name || cachedProfile.email }
        };
        return;
      }
      
      cachedUser = null;
      cachedProfile = null;
    } finally {
      userPromise = null;
    }
  })();
  
  await userPromise;
  return { user: cachedUser, profile: cachedProfile };
};

/**
 * 清除快取（登出時使用）
 */
export const clearUserCache = () => {
  cachedUser = null;
  cachedProfile = null;
  userPromise = null;
  profilePromise = null;
};

/**
 * 更新 profile 快取
 */
export const updateProfileCache = (profile) => {
  cachedProfile = profile;
  
  // 🔑 如果有 profile 但沒有 user，自動生成 user（嵌入模式）
  if (profile && !cachedUser) {
    cachedUser = {
      id: profile.id,
      email: profile.email,
      user_metadata: { name: profile.name || profile.email }
    };
  }
};

/**
 * 更新 user 快取
 */
export const updateUserCache = (user) => {
  cachedUser = user;
};

